/**
 * AiAssetsEditor (Sprint 16 — part B) — reusable AI asset slots editor.
 *
 * Extracted from `src/modules/reactFlow/components/onboarding/editorSectionsV2/
 * AiAssetsSection.tsx`, decoupled from `FlowStepNode` so it can render for:
 *   - Screen step nodes (will migrate later)
 *   - PostExample.aiAssets (this sprint)
 *   - ContentPost.aiAssets (this sprint)
 *
 * Contract:
 *   - `aiAssets: Record<slotKey, AiAssetSlot>` — same shape as screens.
 *   - `onChange(next)` — full replacement (caller merges into owning doc).
 *   - `onGenerateImage` — async callback; caller wires it to the image-gen
 *     backend + updates status back to the slot via another onChange call.
 *
 * Extra affordance vs. AiAssetsSection — an "Add slot" control (absent on
 * screens because slots are created by AI during block generation, but
 * post examples + posts need manual slot creation).
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, TextField, Button, Card, CardMedia, Stack, Chip,
  Select, MenuItem, FormControl, InputLabel, IconButton, CircularProgress, Tooltip,
  Dialog, DialogTitle, DialogContent as MuiDialogContent, DialogActions,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ImageIcon from '@mui/icons-material/Image';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { DEFAULT_IMAGE_MODEL, MediaUploadField } from '@modules/ui-components';
import { AvailableImageModelSelector } from '@modules/ai-settings';
import type { AiAssetSlot, AiAssetExperiment } from '../../types';

// ─── Constants ──────────────────────────────────────────────────

const ASPECT_RATIO_OPTIONS = [
  '1:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '21:9',
];

const IMAGE_SIZE_OPTIONS = [
  { value: '512', label: '512px (Fast)' },
  { value: '1K', label: '1K (Default)' },
  { value: '2K', label: '2K (High)' },
  { value: '4K', label: '4K (Ultra)' },
];

const REFERENCE_ROLE_OPTIONS = [
  'style',
  'layout',
  'typography',
  'motif',
  'approved',
  'anchor',
  'object',
  'character',
  'logo',
];

function generateExpId(): string {
  return `exp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function defaultExperiment(): AiAssetExperiment {
  return {
    promptTemplate: '',
    aspectRatio: '1:1',
    imageSize: '1K',
    model: DEFAULT_IMAGE_MODEL,
    status: 'draft',
  } as AiAssetExperiment;
}

function firstExperiment(slot?: AiAssetSlot): AiAssetExperiment | undefined {
  const raw = (slot as any)?.experiments;
  if (!raw) return undefined;
  if (Array.isArray(raw)) return raw[0] as AiAssetExperiment | undefined;
  const selectedId = (slot as any)?.selectedExperimentId;
  if (selectedId && raw[selectedId]) return raw[selectedId] as AiAssetExperiment;
  return Object.values(raw)[0] as AiAssetExperiment | undefined;
}

function orderFromSlotKey(slotKey: string): number {
  const match = slotKey.match(/(?:card|slide|carousel|asset)_(\d+)$/i);
  if (match) return Number(match[1]);
  if (slotKey === 'hero' || slotKey === 'cover') return 0;
  if (slotKey === 'thumbnail') return 1;
  return Number.MAX_SAFE_INTEGER;
}

function assetSlotOrder(slotKey: string, slot?: AiAssetSlot): number {
  const slotOrder = slot?.order;
  if (typeof slotOrder === 'number' && Number.isFinite(slotOrder)) return slotOrder;
  const exp = firstExperiment(slot);
  const expOrder = (exp as any)?.order;
  if (typeof expOrder === 'number' && Number.isFinite(expOrder)) return expOrder;
  const storyboardIndex = (exp as any)?.storyboardCardIndex;
  if (typeof storyboardIndex === 'number' && Number.isFinite(storyboardIndex)) return storyboardIndex;
  return orderFromSlotKey(slotKey);
}

// ─── Public API ──────────────────────────────────────────────────

export interface GenerateImageConfig {
  prompt: string;
  negativePrompt?: string;
  aspectRatio?: string;
  imageSize?: string;
  style?: string;
  model?: string;
  experimentId?: string;
  referenceImages?: Array<{
    url: string;
    mimeType: string;
    name?: string;
    role?: string;
    instruction?: string;
  }>;
  slotKey: string;
}

export interface GenerateImageResult {
  success: boolean;
  imageUrl?: string;
  generationModel?: string;
  error?: string;
}

export interface AiAssetsEditorProps {
  /** Same shape as screens — `Record<slotKey, AiAssetSlot>`. */
  aiAssets: Record<string, AiAssetSlot>;
  /** Called on every mutation; caller merges into the owning doc. */
  onChange: (next: Record<string, AiAssetSlot>) => void;
  /** Wired to backend image gen. Callback must resolve with final URL
   *  (or fire a task and let another listener update status). */
  onGenerateImage?: (config: GenerateImageConfig) => Promise<GenerateImageResult>;
  /** Uploads a File to remote storage and returns the URL + mimeType.
   *  Used by the main-asset MediaUploadField AND by reference images.
   *  Caller owns the storage path. When omitted, file uploads are
   *  disabled — user can still paste URL strings directly. */
  onUploadReference?: (file: File) => Promise<{ url: string; mimeType: string }>;
  /** Optional — called right after a successful main-asset upload OR
   *  after the user pastes a URL into the main MediaUploadField. Gives
   *  the parent a hook to persist the draft so the backend can
   *  subsequently reach the post doc via dot-notation. */
  onAssetPersist?: (next: Record<string, AiAssetSlot>) => Promise<void> | void;
  /** Project whose connected image providers should populate model choices. */
  projectId?: string;
  disabled?: boolean;
  /** When true, show the "+ Add slot" affordance. Screens set false because
   *  slots are AI-created; post examples + posts set true. */
  allowAddSlot?: boolean;
  testIdPrefix?: string;
}

// ─── Component ──────────────────────────────────────────────────

export const AiAssetsEditor: React.FC<AiAssetsEditorProps> = ({
  aiAssets, onChange, onGenerateImage, onUploadReference, onAssetPersist,
  projectId,
  disabled, allowAddSlot = true,
  testIdPrefix = 'ai-assets-editor',
}) => {
  const [generatingKeys, setGeneratingKeys] = useState<Set<string>>(new Set());
  const [addSlotOpen, setAddSlotOpen] = useState(false);
  const [newSlotKey, setNewSlotKey] = useState('');

  const slotKeys = useMemo(() => Object.keys(aiAssets).sort((a, b) => {
    const byOrder = assetSlotOrder(a, aiAssets[a]) - assetSlotOrder(b, aiAssets[b]);
    return byOrder || a.localeCompare(b);
  }), [aiAssets]);
  const hasSlots = slotKeys.length > 0;

  // ─── Immutable slot helpers ──────────────────────────────────

  const getExperiments = useCallback((slot?: AiAssetSlot): Record<string, AiAssetExperiment> => {
    const raw = (slot as any)?.experiments;
    if (!raw) return {};
    if (!Array.isArray(raw)) return raw as Record<string, AiAssetExperiment>;
    // Legacy array → Record bridge (same normalisation as the screens impl).
    const result: Record<string, AiAssetExperiment> = {};
    (raw as any[]).forEach((exp: AiAssetExperiment) => { result[generateExpId()] = exp; });
    return result;
  }, []);

  const patchSlot = useCallback((slotKey: string, patcher: (prev: AiAssetSlot) => AiAssetSlot) => {
    const prev = aiAssets[slotKey] ?? ({ experiments: {} } as AiAssetSlot);
    const next = patcher(prev);
    onChange({ ...aiAssets, [slotKey]: next });
  }, [aiAssets, onChange]);

  const patchExperiment = useCallback((
    slotKey: string,
    expId: string,
    patcher: (prev: AiAssetExperiment) => AiAssetExperiment,
  ) => {
    patchSlot(slotKey, (slot) => {
      const exps = getExperiments(slot);
      const prev = exps[expId] ?? defaultExperiment();
      return {
        ...slot,
        experiments: { ...exps, [expId]: patcher(prev) },
      } as AiAssetSlot;
    });
  }, [getExperiments, patchSlot]);

  const selectExperiment = useCallback((slotKey: string, expId: string) => {
    patchSlot(slotKey, (slot) => ({ ...slot, selectedExperimentId: expId } as AiAssetSlot));
  }, [patchSlot]);

  const addExperiment = useCallback((slotKey: string) => {
    const newId = generateExpId();
    patchSlot(slotKey, (slot) => {
      const exps = getExperiments(slot);
      return {
        ...slot,
        experiments: { ...exps, [newId]: defaultExperiment() },
        selectedExperimentId: newId,
      } as AiAssetSlot;
    });
  }, [getExperiments, patchSlot]);

  const duplicateExperiment = useCallback((slotKey: string, sourceId: string) => {
    const exps = getExperiments(aiAssets[slotKey]);
    const source = exps[sourceId];
    if (!source) return;
    const newId = generateExpId();
    const copy: AiAssetExperiment = {
      ...JSON.parse(JSON.stringify(source)),
      status: 'draft',
      generatedUrl: undefined,
      generationModel: undefined,
      generatedAt: undefined,
      error: undefined,
    } as AiAssetExperiment;
    patchSlot(slotKey, (slot) => ({
      ...slot,
      experiments: { ...getExperiments(slot), [newId]: copy },
      selectedExperimentId: newId,
    } as AiAssetSlot));
  }, [aiAssets, getExperiments, patchSlot]);

  const deleteExperiment = useCallback((slotKey: string, expId: string) => {
    const exps = getExperiments(aiAssets[slotKey]);
    const ids = Object.keys(exps);
    if (ids.length <= 1) return;
    const { [expId]: _drop, ...rest } = exps;
    const remaining = Object.keys(rest);
    const currentSelected = aiAssets[slotKey]?.selectedExperimentId;
    patchSlot(slotKey, (slot) => ({
      ...slot,
      experiments: rest,
      selectedExperimentId: currentSelected === expId ? remaining[0] : currentSelected,
    } as AiAssetSlot));
  }, [aiAssets, getExperiments, patchSlot]);

  const deleteSlot = useCallback((slotKey: string) => {
    const { [slotKey]: _drop, ...rest } = aiAssets;
    onChange(rest);
  }, [aiAssets, onChange]);

  // ─── Generate ────────────────────────────────────────────────

  const handleGenerate = useCallback(async (slotKey: string, expId: string) => {
    if (!onGenerateImage) return;
    const exps = getExperiments(aiAssets[slotKey]);
    const exp = exps[expId];
    if (!exp?.promptTemplate?.trim()) return;

    const genKey = `${slotKey}:${expId}`;
    setGeneratingKeys((prev) => new Set(prev).add(genKey));
    patchExperiment(slotKey, expId, (prev) => ({ ...prev, status: 'generating' } as AiAssetExperiment));

    try {
      const result = await onGenerateImage({
        slotKey,
        prompt: exp.promptTemplate!,
        negativePrompt: exp.negativePrompt,
        aspectRatio: exp.aspectRatio || '1:1',
        imageSize: exp.imageSize || '1K',
        style: exp.style,
        model: exp.model || DEFAULT_IMAGE_MODEL,
        referenceImages: exp.referenceImages as any,
        experimentId: expId,
      });

      if (result.success && result.imageUrl) {
        // Synchronous callback style — update status immediately.
        patchExperiment(slotKey, expId, (prev) => ({
          ...prev,
          status: 'completed',
          generatedUrl: result.imageUrl,
          generationModel: result.generationModel,
          generatedAt: new Date().toISOString() as any,
        } as AiAssetExperiment));
      } else if (!result.success) {
        patchExperiment(slotKey, expId, (prev) => ({
          ...prev,
          status: 'error',
          error: result.error || 'Generation failed',
        } as AiAssetExperiment));
      }
      // If success: true but no imageUrl — fire-and-forget style; external
      // subscriber will flip status to completed later. Leave as 'generating'.
    } catch (err) {
      patchExperiment(slotKey, expId, (prev) => ({
        ...prev,
        status: 'error',
        error: err instanceof Error ? err.message : 'Unknown error',
      } as AiAssetExperiment));
    } finally {
      setGeneratingKeys((prev) => {
        const next = new Set(prev);
        next.delete(genKey);
        return next;
      });
    }
  }, [aiAssets, getExperiments, onGenerateImage, patchExperiment]);

  // ─── Add slot dialog ─────────────────────────────────────────

  const handleAddSlot = () => {
    const k = newSlotKey.trim();
    if (!k || aiAssets[k]) return;
    const expId = generateExpId();
    onChange({
      ...aiAssets,
      [k]: {
        order: slotKeys.length,
        experiments: { [expId]: defaultExperiment() },
        selectedExperimentId: expId,
      } as AiAssetSlot,
    });
    setNewSlotKey('');
    setAddSlotOpen(false);
  };

  // ─── Render ──────────────────────────────────────────────────

  return (
    <Box data-testid={`${testIdPrefix}-root`} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2">
          AI Assets{hasSlots ? ` (${slotKeys.length} slot${slotKeys.length === 1 ? '' : 's'})` : ''}
        </Typography>
        {allowAddSlot && (
          <Button
            size="small" startIcon={<AddIcon />} onClick={() => setAddSlotOpen(true)} disabled={disabled}
            data-testid={`${testIdPrefix}-add-slot`}
          >
            Add slot
          </Button>
        )}
      </Stack>

      {!hasSlots && (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <ImageIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No AI asset slots yet.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {allowAddSlot
              ? 'Add a slot to describe a visual (hero, thumbnail, carousel_0…) and generate it with AI.'
              : 'Slots are created during AI generation.'}
          </Typography>
        </Box>
      )}

      {slotKeys.map((slotKey) => {
        const slot = aiAssets[slotKey];
        const experiments = getExperiments(slot);
        const entries = Object.entries(experiments);
        const selectedId = slot.selectedExperimentId ?? entries[0]?.[0];
        const activeExp = selectedId ? experiments[selectedId] : undefined;
        // v1.8.0 — fold both local optimistic state AND server-reported
        // 'generating' status. Fire-and-forget image gen returns from
        // onGenerateImage immediately; without folding the server status,
        // the spinner disappears in ~200ms while the backend continues
        // working for 10-30s and the user sees "Generate" again.
        const isGenerating = (selectedId ? generatingKeys.has(`${slotKey}:${selectedId}`) : false)
          || activeExp?.status === 'generating';
        const hasPrompt = !!activeExp?.promptTemplate?.trim();

        return (
          <Card key={slotKey} variant="outlined" sx={{ p: 1.5 }} data-testid={`${testIdPrefix}-slot-${slotKey}`}>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Chip label={slotKey} size="small" variant="outlined" color="primary" />
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {activeExp?.status === 'completed' && (
                    <Chip icon={<CheckCircleIcon />} label="Generated" size="small" color="success" variant="outlined" />
                  )}
                  {allowAddSlot && (
                    <Tooltip title="Remove slot">
                      <IconButton size="small" color="error" onClick={() => deleteSlot(slotKey)} disabled={disabled}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Stack>

              {/* Experiment thumbnail tabs */}
              <Stack direction="row" spacing={0.5} sx={{ overflowX: 'auto', pb: 0.5 }} alignItems="center">
                {entries.map(([expId, exp]) => (
                  <Box
                    key={expId}
                    onClick={() => selectExperiment(slotKey, expId)}
                    sx={{
                      width: 64, height: 64, minWidth: 64,
                      borderRadius: 1, border: 2, overflow: 'hidden',
                      borderColor: expId === selectedId ? 'primary.main' : 'divider',
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: 'action.hover', position: 'relative',
                    }}
                  >
                    {exp.status === 'completed' && exp.generatedUrl ? (
                      <Box component="img" src={exp.generatedUrl} alt={expId} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : exp.status === 'generating' ? (
                      <CircularProgress size={20} />
                    ) : exp.status === 'error' ? (
                      <ErrorIcon color="error" fontSize="small" />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {entries.findIndex(([id]) => id === expId) + 1}
                      </Typography>
                    )}
                  </Box>
                ))}
                <Tooltip title="Add experiment">
                  <IconButton size="small" onClick={() => addExperiment(slotKey)} disabled={disabled}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>

              {activeExp && selectedId && (
                <>
                  {/* Image preview — shown only when we have a URL. Kept
                      separate from the editor below so it renders instantly
                      without re-mounting the MediaUploadField on generation. */}
                  {activeExp.generatedUrl && (
                    <CardMedia
                      component="img"
                      image={activeExp.generatedUrl}
                      alt={`Generated ${slotKey}`}
                      sx={{ borderRadius: 1, maxHeight: 300, objectFit: 'contain', bgcolor: 'action.hover' }}
                    />
                  )}

                  {/* Asset URL editor — same MediaUploadField that the
                      onboarding ImageUploadElement uses. Three input paths:
                        (a) paste URL directly → commits on change
                        (b) upload file via picker → onUploadReference → URL
                        (c) leave empty + hit Generate below → AI fills it
                      All three routes write into `generatedUrl + status:
                      completed` on the active experiment, so the surrounding
                      thumbnail tab + generation UI reflects the final state
                      uniformly. */}
                  <Box>
                    <MediaUploadField
                      value={activeExp.generatedUrl ?? null}
                      mediaType="image"
                      size="small"
                      showPreview={!activeExp.generatedUrl}
                      placeholder="Paste image URL or upload a file"
                      disabled={disabled}
                      onChange={async (value: string | File | null) => {
                        // Resolve the incoming value to a URL+mime pair:
                        //   File  → upload via caller's onUploadReference
                        //   null  → clear the experiment's generatedUrl
                        //   strng → take as-is
                        let resolvedUrl: string | null = null;
                        let resolvedMime: string | undefined;
                        if (value instanceof File) {
                          if (!onUploadReference) return;
                          try {
                            const r = await onUploadReference(value);
                            resolvedUrl = r.url;
                            resolvedMime = r.mimeType;
                          } catch (err) {
                            // Surface error on the experiment so the
                            // thumbnail gets the red icon + error text.
                            patchExperiment(slotKey, selectedId, (prev) => ({
                              ...prev,
                              status: 'error',
                              error: (err as Error).message,
                            } as AiAssetExperiment));
                            return;
                          }
                        } else if (typeof value === 'string') {
                          resolvedUrl = value.trim() || null;
                        }

                        // Apply to the experiment — treat user-provided URLs
                        // as 'completed' so the Generate button flips into
                        // "Regenerate" state and Save will persist them.
                        let nextAssets: Record<string, AiAssetSlot> | null = null;
                        patchExperiment(slotKey, selectedId, (prev) => ({
                          ...prev,
                          generatedUrl: resolvedUrl ?? undefined,
                          status: resolvedUrl ? ('completed' as any) : ('draft' as any),
                          generationModel: resolvedUrl ? ((prev as any).generationModel ?? 'manual_upload') : undefined,
                          generatedAt: resolvedUrl ? (new Date().toISOString() as any) : undefined,
                          error: undefined,
                          ...(resolvedMime ? { mimeType: resolvedMime } as any : {}),
                        } as AiAssetExperiment));

                        // Rebuild the full aiAssets object post-patch so the
                        // caller's onAssetPersist sees the updated state.
                        nextAssets = JSON.parse(JSON.stringify(aiAssets));
                        const slot = nextAssets![slotKey];
                        if (slot) {
                          const exps = (slot as any).experiments ?? {};
                          exps[selectedId] = {
                            ...exps[selectedId],
                            generatedUrl: resolvedUrl ?? undefined,
                            status: resolvedUrl ? 'completed' : 'draft',
                            generationModel: resolvedUrl ? (exps[selectedId]?.generationModel ?? 'manual_upload') : undefined,
                            generatedAt: resolvedUrl ? new Date().toISOString() : undefined,
                            error: undefined,
                          };
                        }
                        if (onAssetPersist && nextAssets) {
                          try { await onAssetPersist(nextAssets); } catch { /* non-critical */ }
                        }
                      }}
                    />
                    {activeExp.generatedUrl && (
                      <Typography
                        variant="caption" color="text.secondary"
                        sx={{ display: 'block', mt: 0.25, fontFamily: 'monospace', fontSize: '0.65rem', wordBreak: 'break-all' }}
                        onClick={() => { void navigator.clipboard.writeText(activeExp.generatedUrl!); }}
                      >
                        {activeExp.generatedUrl}
                      </Typography>
                    )}
                  </Box>

                  <TextField
                    label="Image prompt"
                    value={activeExp.promptTemplate ?? ''}
                    onChange={(e) => patchExperiment(slotKey, selectedId, (prev) => ({ ...prev, promptTemplate: e.target.value } as AiAssetExperiment))}
                    multiline minRows={2} maxRows={6} fullWidth size="small"
                    disabled={disabled} placeholder="Describe the image for AI generation..."
                  />

                  <Stack direction="row" spacing={1}>
                    <FormControl size="small" sx={{ minWidth: 90 }}>
                      <InputLabel>Ratio</InputLabel>
                      <Select
                        value={activeExp.aspectRatio ?? '1:1'} label="Ratio"
                        onChange={(e) => patchExperiment(slotKey, selectedId, (prev) => ({ ...prev, aspectRatio: e.target.value } as AiAssetExperiment))}
                        disabled={disabled}
                      >
                        {ASPECT_RATIO_OPTIONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 110 }}>
                      <InputLabel>Size</InputLabel>
                      <Select
                        value={activeExp.imageSize ?? '1K'} label="Size"
                        onChange={(e) => patchExperiment(slotKey, selectedId, (prev) => ({ ...prev, imageSize: e.target.value } as AiAssetExperiment))}
                        disabled={disabled}
                      >
                        {IMAGE_SIZE_OPTIONS.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Style" value={activeExp.style ?? ''}
                      onChange={(e) => patchExperiment(slotKey, selectedId, (prev) => ({ ...prev, style: e.target.value } as AiAssetExperiment))}
                      size="small" placeholder="e.g., photorealistic" disabled={disabled} sx={{ flex: 1 }}
                    />
                  </Stack>

                  <AvailableImageModelSelector
                    projectId={projectId}
                    value={activeExp.model || DEFAULT_IMAGE_MODEL}
                    onChange={(nextModel) => patchExperiment(
                      slotKey,
                      selectedId,
                      (prev) => ({ ...prev, model: nextModel } as AiAssetExperiment)
                    )}
                    disabled={disabled}
                    fullWidth
                  />

                  <TextField
                    label="Negative prompt" value={activeExp.negativePrompt ?? ''}
                    onChange={(e) => patchExperiment(slotKey, selectedId, (prev) => ({ ...prev, negativePrompt: e.target.value } as AiAssetExperiment))}
                    size="small" fullWidth placeholder="What to exclude..." disabled={disabled}
                  />

                  {/* Reference images — up to 14 per Gemini API limits. Paired
                      with the `onUploadReference` callback for actual GCS
                      uploads when File is picked; URL strings are kept as-is. */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Reference images ({activeExp.referenceImages?.length ?? 0}/14)
                    </Typography>
                    <Stack spacing={1}>
                      {(activeExp.referenceImages ?? []).map((ref: any, refIdx: number) => (
                        <Box
                          key={refIdx}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 1,
                          }}
                        >
                          <Stack spacing={1}>
                            <MediaUploadField
                              value={ref.url || null}
                              onChange={async (value: string | File | null) => {
                                // Resolve URL — if File, upload via caller's callback;
                                // if string/null, use directly.
                                let resolved: { url: string; mimeType: string } | null = null;
                                if (value instanceof File) {
                                  if (!onUploadReference) return;
                                  try {
                                    resolved = await onUploadReference(value);
                                  } catch {
                                    return;
                                  }
                                } else if (value) {
                                  resolved = { url: value, mimeType: ref.mimeType || 'image/png' };
                                }
                                patchExperiment(slotKey, selectedId, (prev) => {
                                  const refs = [...(prev.referenceImages ?? [])];
                                  if (resolved) {
                                    refs[refIdx] = { ...refs[refIdx], url: resolved.url, mimeType: resolved.mimeType };
                                  } else {
                                    refs.splice(refIdx, 1);
                                  }
                                  return { ...prev, referenceImages: refs } as AiAssetExperiment;
                                });
                              }}
                              mediaType="image"
                              disabled={disabled}
                              size="small"
                              showPreview
                              placeholder={`Reference image ${refIdx + 1}`}
                            />
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                              <TextField
                                label="Reference name"
                                value={ref.name ?? ''}
                                onChange={(e) => patchExperiment(slotKey, selectedId, (prev) => {
                                  const refs = [...(prev.referenceImages ?? [])] as any[];
                                  refs[refIdx] = { ...refs[refIdx], name: e.target.value };
                                  return { ...prev, referenceImages: refs } as AiAssetExperiment;
                                })}
                                size="small"
                                disabled={disabled}
                                sx={{ flex: 1 }}
                              />
                              <FormControl size="small" sx={{ minWidth: 140 }}>
                                <InputLabel>Role</InputLabel>
                                <Select
                                  label="Role"
                                  value={ref.role ?? 'style'}
                                  onChange={(e) => patchExperiment(slotKey, selectedId, (prev) => {
                                    const refs = [...(prev.referenceImages ?? [])] as any[];
                                    refs[refIdx] = { ...refs[refIdx], role: e.target.value };
                                    return { ...prev, referenceImages: refs } as AiAssetExperiment;
                                  })}
                                  disabled={disabled}
                                >
                                  {REFERENCE_ROLE_OPTIONS.map((role) => (
                                    <MenuItem key={role} value={role}>{role}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Stack>
                            <TextField
                              label="How to use this reference"
                              value={ref.instruction ?? ''}
                              onChange={(e) => patchExperiment(slotKey, selectedId, (prev) => {
                                const refs = [...(prev.referenceImages ?? [])] as any[];
                                refs[refIdx] = { ...refs[refIdx], instruction: e.target.value };
                                return { ...prev, referenceImages: refs } as AiAssetExperiment;
                              })}
                              size="small"
                              multiline
                              minRows={2}
                              disabled={disabled}
                              placeholder="Use palette and layout rhythm only; do not copy the content."
                            />
                          </Stack>
                        </Box>
                      ))}
                      {(activeExp.referenceImages?.length ?? 0) < 14 && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ImageOutlinedIcon />}
                          disabled={disabled}
                          onClick={() => {
                            patchExperiment(slotKey, selectedId, (prev) => {
                              const refs = [...(prev.referenceImages ?? [])];
                              refs.push({ url: '', mimeType: 'image/png', role: 'style', name: '', instruction: '' } as any);
                              return { ...prev, referenceImages: refs } as AiAssetExperiment;
                            });
                          }}
                        >
                          Add reference
                        </Button>
                      )}
                    </Stack>
                  </Box>

                  {activeExp.generationModel && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip label={activeExp.generationModel} size="small" variant="outlined" />
                      {activeExp.generatedAt && (
                        <Chip label={new Date(activeExp.generatedAt as any).toLocaleString()} size="small" variant="outlined" />
                      )}
                    </Stack>
                  )}

                  {activeExp.status === 'error' && activeExp.error && (
                    <Typography variant="caption" color="error">{activeExp.error}</Typography>
                  )}

                  <Stack direction="row" spacing={1}>
                    <Button
                      startIcon={isGenerating ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                      variant="outlined" size="small" sx={{ flex: 1 }}
                      disabled={disabled || isGenerating || !hasPrompt || !onGenerateImage}
                      onClick={() => handleGenerate(slotKey, selectedId)}
                      data-testid={`${testIdPrefix}-generate-${slotKey}`}
                    >
                      {isGenerating ? 'Generating...' : activeExp.status === 'completed' ? 'Regenerate' : 'Generate'}
                    </Button>
                    <Tooltip title="Duplicate experiment">
                      <IconButton size="small" onClick={() => duplicateExperiment(slotKey, selectedId)} disabled={disabled}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={entries.length <= 1 ? 'Cannot delete last experiment' : 'Delete experiment'}>
                      <span>
                        <IconButton
                          size="small" color="error"
                          onClick={() => deleteExperiment(slotKey, selectedId)}
                          disabled={disabled || entries.length <= 1}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </>
              )}
            </Stack>
          </Card>
        );
      })}

      <Dialog open={addSlotOpen} onClose={() => setAddSlotOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add AI asset slot</DialogTitle>
        <MuiDialogContent>
          <TextField
            autoFocus fullWidth size="small" sx={{ mt: 1 }}
            label="Slot key"
            placeholder="hero | thumbnail | carousel_0"
            value={newSlotKey}
            onChange={(e) => setNewSlotKey(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddSlot(); }}
            helperText="Letters, numbers, underscore only. Common names: hero, thumbnail, carousel_0, avatar."
          />
        </MuiDialogContent>
        <DialogActions>
          <Button onClick={() => setAddSlotOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddSlot} disabled={!newSlotKey.trim() || Boolean(aiAssets[newSlotKey.trim()])}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AiAssetsEditor;
